import { useEffect, useState } from 'react';
import { Button, Divider, Form, Upload } from 'antd';
import { useAppDispatch, useAppSelector } from '@hooks/redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { getAccountBalanceApi, getSenderAccountsApi } from '@http/accounts';
import { swiftTransactionApi } from '@http/payments';
import { getTemplatesApi } from '@http/user';
import { getCountriesApi } from '@http/global';

import SenderAccountsModal from '@components/Payments/modals/SenderAccountsModal';
import SearchSwiftModal from '@components/Payments/modals/SearchSwiftModal';
import ConfirmPaymentModal from '@components/Payments/modals/ConfirmPaymentModal';
import Currencies from '@components/Currency/OrienbankRates';
import Templates from '@components/Templates/Templates';
import SaveTemplateModal from '@components/Payments/modals/SaveTemplateModal';

import { deadlineAC, otpConfirmAC, repeatSwiftTransactionAC, verifyTransIdAC } from '@store/reducers/transactions';
import { pickedSwiftTemplateAC } from '@store/reducers/user';
import {
  isConfirmPaymentModalOpenAC,
  isSaveTemplateModalOpenAC,
  isSearchSwiftModalOpenAC,
  newSwiftTemplateAC,
  searchedSwiftAC,
  swiftQueryAC,
} from '@store/reducers/payments';
import { senderAccountBalanceAC, selectedSenderAccountAC, senderAccountsModalOpenAC } from '@store/reducers/accounts';
import SwiftForm from '@components/Payments/forms/SwiftForm';
import SelectCounterparty from '@components/Counterparties/SelectCounterparty';
import { UploadOutlined } from '@ant-design/icons';

function Swift() {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const senderAccounts = useAppSelector(state => state.accountsReducer.senderAccounts);

  const pickedSwiftTemplate = useAppSelector(state => state.userReducer.pickedSwiftTemplate);
  const repeatSwiftTransaction = useAppSelector(state => state.transactionsReducer.repeatSwiftTransaction);
  const newSwiftTemplate = useAppSelector(state => state.paymentsReducer.newSwiftTemplate);
  const swiftQuery = useAppSelector(state => state.paymentsReducer.swiftQuery);
  const permissions = useAppSelector(state => state.userReducer.permissions);
  const senderAccountBalance = useAppSelector(state => state.accountsReducer.senderAccountBalance);

  const [senderAccountCurrency, setSenderAccountCurrency] = useState('');
  const [file, setFile] = useState<any>();

  useEffect(() => {
    initializeData();

    return clearFields;
  }, []);

  useEffect(() => {
    pickedSwiftTemplateHandler();
  }, [pickedSwiftTemplate]);

  useEffect(() => {
    repeatSwiftTransactionHandler();
  }, [repeatSwiftTransaction]);

  useEffect(() => {
    dispatch(otpConfirmAC(false));
  }, [swiftQuery]);

  const initializeData = () => {
    dispatch(getCountriesApi());
    dispatch(searchedSwiftAC([]));
    dispatch(selectedSenderAccountAC({}));
    dispatch(senderAccountBalanceAC(0));
    dispatch(getTemplatesApi({ transaction_type: 'SWIFT' }));
    dispatch(
      getSenderAccountsApi({
        transaction_type: 'SWIFT',
      })
    );
  };

  const pickedSwiftTemplateHandler = () => {
    setSenderAccountCurrency(pickedSwiftTemplate.sending_ccy);

    fillQuery(pickedSwiftTemplate, 'template');
    fillForms(pickedSwiftTemplate, 'template');

    if (+pickedSwiftTemplate.sending_amount > 0) {
      dispatch(getAccountBalanceApi('sender', { cust_ac_no: pickedSwiftTemplate.sending_ac }));
    }
  };

  const repeatSwiftTransactionHandler = () => {
    setSenderAccountCurrency(repeatSwiftTransaction.sending_ccy);

    fillForms(repeatSwiftTransaction, 'repeat');
    fillQuery(repeatSwiftTransaction, 'repeat');

    if (+repeatSwiftTransaction.sending_amount > 0) {
      dispatch(getAccountBalanceApi('sender', { cust_ac_no: repeatSwiftTransaction.sending_ac }));
    }
  };

  const fillForms = (data: any, type: any) => {
    form.setFieldValue('sending_ac', data.sending_ac);
    form.setFieldValue('receiving_ac', data.receiving_ac);
    form.setFieldValue('receiving_ac2', data.receiving_ac);
    form.setFieldValue('receiver_name', data.receiver_name);
    form.setFieldValue('sender_address', data.sender_address);
    form.setFieldValue('sender_city', data.sender_city);
    form.setFieldValue('receiver_city', data.receiver_city);
    form.setFieldValue('receiver_country', data.receiver_country);
    form.setFieldValue('receiver_bank_swift', data.receiver_bank_swift);
    form.setFieldValue('receiver_bank_name', data.receiver_bank_name);
    form.setFieldValue('receiver_bank_country', data.receiver_bank_country);
    form.setFieldValue('receiver_address', data.receiver_address);

    dispatch(selectedSenderAccountAC(data?.sending_ac));
  };

  const fillQuery = (data: any, type: any) => {
    dispatch(
      swiftQueryAC({
        sending_ac: data.sending_ac,
        receiving_ac: data.receiving_ac,
        receiving_ac2: data.receiving_ac,
        receiver_name: data.receiver_name,
        doc_no: data.doc_no,
        receiver_bank_name: data.receiver_bank_name,
        receiver_bank_swift: data.receiver_bank_swift,
        receiver_country: data.receiver_country,
        receiver_city: data.receiver_city,
        sender_city: data.sender_city,
        sender_address: data.sender_address,
        receiver_bank_country: data.receiver_bank_country,
        receiver_address: data.receiver_address,
      })
    );
  };

  const senderAccountSelect = (account: any) => {
    setSenderAccountCurrency(account.ccy);
    dispatch(swiftQueryAC({ ...swiftQuery, sending_ac: account.value }));

    form.setFieldValue('sending_ac', account.value);

    dispatch(senderAccountsModalOpenAC(false));
    dispatch(selectedSenderAccountAC(account));
    dispatch(getAccountBalanceApi('sender', { cust_ac_no: account.value }));
  };

  const selectSwiftHandler = (item: any) => {
    form.setFieldsValue({
      receiver_bank_swift: item.bic_code,
      receiver_bank_name: item.bank_name,
    });

    dispatch(isSearchSwiftModalOpenAC(false));
  };

  const swiftTransaction = () => {
    dispatch(swiftTransactionApi({ ...swiftQuery, file: file?.originFileObj }, navigate, () => clearFields()));
  };

  const clearFields = () => {
    dispatch(swiftQueryAC({}));
    setSenderAccountCurrency('');
    setFile(null);

    form.resetFields();

    dispatch(otpConfirmAC(false));
    dispatch(verifyTransIdAC(''));
    dispatch(senderAccountBalanceAC(0));
    dispatch(selectedSenderAccountAC({}));
    dispatch(isConfirmPaymentModalOpenAC(false));
    dispatch(repeatSwiftTransactionAC({}));
    dispatch(pickedSwiftTemplateAC({}));
  };

  const submit = (values: any) => {
    const { file, ...query } = values;

    if (+swiftQuery.sending_amount > +senderAccountBalance) {
      form.setFields([
        {
          name: 'sending_ac',
          errors: [t('Insufficient funds in the account')],
        },
      ]);
    } else {
      dispatch(swiftQueryAC(query));
      dispatch(isConfirmPaymentModalOpenAC(true));
      dispatch(deadlineAC(Date.now() + 1000 * 30));
    }
  };

  const openSaveTemplateModal = () => {
    dispatch(
      newSwiftTemplateAC({
        ...newSwiftTemplate,
        sending_amount: form.getFieldValue('sending_amount'),
        sending_ac: form.getFieldValue('sending_ac'),
        receiving_ac: form.getFieldValue('receiving_ac'),
        receiver_name: form.getFieldValue('receiver_name'),
        sending_ccy: senderAccountCurrency,
        narrative: form.getFieldValue('narrative'),
        doc_no: form.getFieldValue('doc_no'),
        sender_country: form.getFieldValue('sender_country'),
        sender_city: form.getFieldValue('sender_city'),
        sender_address: form.getFieldValue('sender_address'),
        receiver_country: form.getFieldValue('receiver_country'),
        receiver_city: form.getFieldValue('receiver_city'),
        receiver_bank_swift: form.getFieldValue('receiver_bank_swift'),
        receiver_bank_name: form.getFieldValue('receiver_bank_name'),
        receiver_bank_country: form.getFieldValue('receiver_bank_country'),
        receiver_address: form.getFieldValue('receiver_address'),
      })
    );

    dispatch(isSaveTemplateModalOpenAC(true));
  };

  const dummyRequest = ({ onSuccess }: any) => {
    setTimeout(() => {
      onSuccess('ok');
    }, 0);
  };

  return (
    <div className='md:grid grid-cols-6 gap-10 mt-5'>
      <div className='col-span-4'>
        {permissions?.cpty ? (
          <div>
            {permissions?.cpty[0].allow_view ? (
              <div className=' mt-7'>
                <SelectCounterparty type='SWIFT' />
              </div>
            ) : null}
          </div>
        ) : null}

        <div className=' mt-7'>
          <Templates type='SWIFT' />
        </div>
        <Divider className='my-6' />
        <SwiftForm form={form} senderAccountCurrency={senderAccountCurrency} />
        <Form form={form} name='SwiftDocument' onFinish={submit}>
          <p className='text-bs color-title mt-5 md:hidden'>{t('Document')}:</p>
          <div className='md:grid grid-cols-7 md:mt-7'>
            <p className='col-span-2 text-bs color-title max-md:hidden'>{t('Document')}:</p>
            <div className='col-span-5'>
              <Form.Item
                name='file'
                className='m-0'
                valuePropName='fileList'
                getValueFromEvent={e => e?.fileList}
                rules={[{ required: true, type: 'array', message: `${t('Required')}` }]}
              >
                <Upload
                  onChange={e => {
                    setFile(e.file);
                  }}
                  customRequest={dummyRequest}
                  listType='picture'
                  accept='.pdf'
                  maxCount={1}
                >
                  <Button icon={<UploadOutlined />}>{t('Upload')}</Button>
                </Upload>
              </Form.Item>
            </div>
          </div>

          <div className='flex justify-end mt-5'>
            <Button className='mr-3' type='default' onClick={openSaveTemplateModal}>
              {t('Save template')}
            </Button>
            <Button type='primary' htmlType='submit'>
              {t('Confirm')}
            </Button>
          </div>
        </Form>
        <SenderAccountsModal accounts={senderAccounts} selectHandler={senderAccountSelect} />
        <SearchSwiftModal selectSwiftHandler={selectSwiftHandler} />
        <SaveTemplateModal template={newSwiftTemplate} />
        <ConfirmPaymentModal
          title={`${t('International')}`}
          paymentInfo={swiftQuery}
          currency={senderAccountCurrency}
          paymentTransactionHandler={swiftTransaction}
          clearFields={clearFields}
          route={'swiftPaymentStatus'}
          type='SWIFT'
        />
      </div>
      <div className='col-span-2 max-md:hidden'>
        <Currencies size='max-lg:text-xs text-sm' />

        <div className='bg-[#fffbea] p-6 rounded-lg' style={{ border: '1px solid #ffd230' }}>
          <div className='leading-7'>
            <p className='text-base font-semibold'>{t('swiftAttention.title')}</p>
            <p className='text-sm mt-3'>{t('swiftAttention.dearClients')}</p>
            <p className='text-sm'>
              {t('swiftAttention.payAttention')}
              <span className='font-semibold'>{t('swiftAttention.narrative')}</span>. <br /> {t('swiftAttention.file')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Swift;
